import { isRecord } from "../types/data";
import { useEffect, useRef } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { useFirebase } from "../context/FirebaseContext";
import { isDemoUserId } from "../utility/demoSession";
import { dispatchExperienceEvents } from "../utility/experienceNotification";
import { settleAllPendingImportExperience } from "../utility/importExperienceOutbox";
import { invalidateCachedValue } from "../utility/localDataCache";
import { getUserProfileCacheKey } from "../utility/profileCache";

// Import checkpoints are stored in Firestore beside the notification root.
// Recovery probes that one authoritative document before calling the more
// expensive settlement endpoint; browser cache state can neither lose nor
// duplicate a committed checkpoint.
function ImportExperienceRecovery({ userId }: { userId?: string | null }) {
  const { auth, firebase, firestore } = useFirebase();
  const [authUser] = useAuthState(auth);
  const inFlightRef = useRef(false);
  const rerunRequestedRef = useRef(false);
  const retryAfterProfileUpdateRef = useRef(false);
  const ready = Boolean(
    userId &&
    authUser?.uid === userId &&
    !isDemoUserId(userId)
  );

  useEffect(() => {
    if (!ready || !userId) return undefined;
    let cancelled = false;

    const recover = async ({ verifyOutbox = true }: { verifyOutbox?: boolean } = {}) => {
      if (cancelled) return;
      if (inFlightRef.current) {
        rerunRequestedRef.current = true;
        return;
      }
      inFlightRef.current = true;
      try {
        do {
          rerunRequestedRef.current = false;
          if (verifyOutbox) {
            const rootSnapshot = await firestore
              .collection("userNotifications")
              .doc(userId)
              .get({ source: "server" });
            const checkpoints: unknown = rootSnapshot.exists
              ? rootSnapshot.data()?.importExperienceOutbox?.checkpoints
              : null;
            if (
              !checkpoints ||
              typeof checkpoints !== "object" ||
              Object.keys(checkpoints).length === 0
            ) {
              retryAfterProfileUpdateRef.current = false;
              continue;
            }
          }
          const result = await settleAllPendingImportExperience({
            firestore,
            firebase,
            user: authUser
          });
          retryAfterProfileUpdateRef.current =
            result?.reason === "missing-experience-baseline" ||
            result?.pendingReason === "missing-experience-baseline";
          if (cancelled) return;
          dispatchExperienceEvents(result?.events || [], userId);
          if (result?.publicStats && typeof window !== "undefined") {
            void invalidateCachedValue(getUserProfileCacheKey(userId));
            window.dispatchEvent(new CustomEvent(
              "movie-profile-experience-updated",
              {
                detail: {
                  userId,
                  publicStats: result.publicStats,
                  source: "import-experience-recovery"
                }
              }
            ));
          }
        } while (!cancelled && rerunRequestedRef.current);
      } catch (error) {
        // The server checkpoint is intentionally left pending. Reconnect, a
        // new import signal or the next authenticated mount retries it.
        console.warn("Не удалось восстановить XP после импорта:", error);
      } finally {
        inFlightRef.current = false;
      }
    };

    const handleRetry = (event: Event) => {
      const detail: unknown = event instanceof CustomEvent ? event.detail : null;
      const eventData = isRecord(detail) ? detail : {};
      const eventUserId = eventData.userId;
      if (eventUserId && eventUserId !== userId) return;
      if (eventData.source === "import-experience-recovery") return;
      if (
        event?.type === "movie-profile-experience-updated" &&
        !retryAfterProfileUpdateRef.current &&
        !inFlightRef.current
      ) return;
      void recover({
        verifyOutbox: event?.type !== "import-experience-checkpoint-pending" &&
          !retryAfterProfileUpdateRef.current
      });
    };

    void recover();
    if (typeof window !== "undefined") {
      window.addEventListener("online", handleRetry);
      window.addEventListener("import-experience-checkpoint-pending", handleRetry);
      window.addEventListener("movie-profile-experience-updated", handleRetry);
    }
    return () => {
      cancelled = true;
      if (typeof window !== "undefined") {
        window.removeEventListener("online", handleRetry);
        window.removeEventListener("import-experience-checkpoint-pending", handleRetry);
        window.removeEventListener("movie-profile-experience-updated", handleRetry);
      }
    };
  }, [authUser, firebase, firestore, ready, userId]);

  return null;
}

export default ImportExperienceRecovery;
